<template>
  <div class="h-full flex flex-col">
    <!-- Header (Dynamic based on internal step) -->
    <div v-if="localStep === 1" class="text-center space-y-2 mb-6">
       <div class="flex items-center justify-between mb-2">
            <button 
                v-if="showBack"
                @click="$emit('back')"
                class="flex items-center text-slate-400 hover:text-slate-600 transition-colors text-sm font-medium"
            >
                <ChevronLeft class="w-4 h-4 mr-1" />
                Back
            </button>
            <div v-else></div> <!-- Spacer -->
       </div>
       <h1 class="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">What is your role?</h1>
       <p class="text-slate-500 dark:text-slate-400">Define your business type in the network.</p>
    </div>

    <!-- Step 1: Role Selection -->
    <div v-if="localStep === 1" class="space-y-6">
        <div class="grid gap-4">
          <!-- Operator Option -->
          <button 
            @click="selectType('operator')"
            class="group relative p-6 bg-white dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 rounded-2xl text-left transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden w-full"
          >
             <div class="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Ship :size="80" class="text-blue-600 dark:text-blue-400 -rotate-12 transform group-hover:scale-110 transition-transform duration-500" />
             </div>
             <div class="flex items-center gap-4 mb-3 relative z-10">
                <div class="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                   <Ship :size="24" />
                </div>
                <div>
                   <h3 class="font-bold text-gray-900 dark:text-white text-lg">Dive Center / Operator</h3>
                   <p class="text-gray-500 dark:text-slate-400 text-sm">Draft a dive site map</p>
                </div>
             </div>
             <p class="text-sm text-gray-600 dark:text-slate-300 leading-relaxed pl-16 relative z-10">I own or manage a dive center, resort, or liveaboard that needs to create maps.</p>
          </button>

          <!-- Partner Option -->
          <button 
            @click="selectType('partner')"
             class="group relative p-6 bg-white dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 hover:border-purple-500 dark:hover:border-purple-500 rounded-2xl text-left transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden w-full"
          >
             <div class="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Building2 :size="80" class="text-purple-600 dark:text-purple-400 -rotate-12 transform group-hover:scale-110 transition-transform duration-500" />
             </div>
             <div class="flex items-center gap-4 mb-3 relative z-10">
                <div class="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition-colors">
                   <Building2 :size="24" />
                </div>
                <div>
                   <h3 class="font-bold text-gray-900 dark:text-white text-lg">Local Retail Shop/Agency</h3>
                   <p class="text-gray-500 dark:text-slate-400 text-sm">Resell trips & training</p>
                </div>
             </div>
             <p class="text-sm text-gray-600 dark:text-slate-300 leading-relaxed pl-16 relative z-10">My inland shop sells trips for my partner and runs a local training center.</p>
          </button>
        </div>
    </div>

    <!-- Step 2: Details Form -->
    <div v-else-if="localStep === 2" class="flex-1 flex flex-col">
        <BusinessDetailsForm 
            :initial-data="formData"
            :show-back="true"
            @back="localStep = 1"
            @submit="handleDetailsSubmit"
        />
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { Building2, Ship, ChevronLeft } from 'lucide-vue-next'
import { coreState } from '../../stores/core.js'
import { useShopRegistration } from '../../stores/useShopRegistration.js'
import BusinessDetailsForm from './BusinessDetailsForm.vue'

const registration = useShopRegistration();

const props = defineProps({
    userId: { type: String, default: null },
    showBack: { type: Boolean, default: false },
    forceNew: { type: Boolean, default: false }
})

const emit = defineEmits(['complete', 'back'])

const isSubmitting = ref(false)

// DB-First: Fetch existing shop from coreState on mount
const existingShop = computed(() => {
    const userId = props.userId || coreState.activeUser?.id;
    if (!userId) return null;

    // [FIX] If forceNew is true, explicitly ignore existing shops to allow creating a new one
    if (props.forceNew) return null;
    
    const membership = [...coreState.members.values()].find(
        m => m.userId === userId && m.role === 'Owner' && m.status === 'active'
    );
    
    return membership ? coreState.shops.get(membership.shopId) : null;
});

// Local form data - initialized from DB/coreState
const formData = reactive({
    id: null,
    type: '',
    name: '',
    website: '',
    location: '',
    coordinates: null,
    bbox: null,
    calibration: {}
});

// Determine initial step based on existing data
const localStep = ref(1);

// Sync form data from existing shop - use watch with immediate to handle both mount and updates

watch(existingShop, (shop) => {
    if (shop) {
        console.log('[Wizard] Syncing form from coreState shop:', shop.id);
        formData.id = shop.id;
        formData.type = shop.type || '';
        formData.name = shop.name || '';
        formData.website = Array.isArray(shop.websites) ? shop.websites.join(', ') : (shop.websites || '');
        formData.location = shop.location || '';
        formData.coordinates = shop.coordinates || null;
        formData.bbox = shop.bbox || null;
        formData.calibration = shop.calibration || {};
        
        // Skip to details form if shop already has a type
        if (formData.type) {
            localStep.value = 2;
        }
    }
}, { immediate: true });

const selectType = (type) => {
    formData.type = type;
    localStep.value = 2;
}

const handleDetailsSubmit = async (data) => {
    // Merge form data
    Object.assign(formData, data);
    
    const targetUserId = props.userId || coreState.activeUser?.id;
    
    if (!targetUserId) {
        console.error("No User ID provided for shop registration.");
        return;
    }

    try {
        isSubmitting.value = true;
        
        let resultShop;
        
        // Check if shop already exists (either from form or coreState lookup)
        const shopId = formData.id || existingShop.value?.id;
        
        console.log('[Wizard] Submit - shopId:', shopId, 'formData.id:', formData.id);

        if (shopId) {
            // UPDATE existing shop in DB
            await registration.updateShop(shopId, {
                name: formData.name,
                location: formData.location,
                website: formData.website,
                coordinates: formData.coordinates,
                type: formData.type
            });
            
            // Update coreState directly
            const shop = coreState.shops.get(shopId);
            if (shop) {
                Object.assign(shop, {
                    name: formData.name,
                    location: formData.location,
                    website: formData.website,
                    coordinates: formData.coordinates,
                    type: formData.type
                });
            }
            
            resultShop = { ...formData, id: shopId };
        } else {
            // CREATE new shop in DB
            const shopPayload = {
                name: formData.name,
                location: formData.location,
                coordinates: formData.coordinates || [0, 0],
                website: formData.website,
                type: formData.type
            };
            resultShop = await registration.registerShop(targetUserId, shopPayload);
            
            // registerShop already updates coreState.shops
            formData.id = resultShop.id;
        }

        // Emit completion - SignUpFlow moves to step 5
        emit('complete', resultShop);

    } catch (e) {
        console.error("Failed to register/update shop:", e);
        alert("Failed to save shop details. Please try again.");
    } finally {
        isSubmitting.value = false;
    }
}
</script>
